/** Segmented progress bar — per-step segments with correct/incorrect/current colour coding. */
import { useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { StudySessionStep } from '@formency/shared';
import { useSession } from '../SessionContext';
import type { AnsweredStep } from '../types';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Below this threshold, show individual segments. At or above, use grouped mode. */
const GROUP_THRESHOLD = 16;
/** Target number of steps per group in grouped mode. */
const TARGET_GROUP_SIZE = 5;

// ─── Types ──────────────────────────────────────────────────────────────────

interface StepGroup {
  startIndex: number;
  endIndex: number; // inclusive
  size: number;
}

// ─── Grouping logic ─────────────────────────────────────────────────────────

function createGroups(total: number): StepGroup[] {
  const numGroups = Math.ceil(total / TARGET_GROUP_SIZE);
  const baseSize = Math.floor(total / numGroups);
  const remainder = total % numGroups;

  const groups: StepGroup[] = [];
  let start = 0;
  for (let i = 0; i < numGroups; i++) {
    const size = baseSize + (i < remainder ? 1 : 0);
    groups.push({ startIndex: start, endIndex: start + size - 1, size });
    start += size;
  }
  return groups;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getStepBackground(
  index: number,
  steps: StudySessionStep[],
  answeredSteps: Map<number, AnsweredStep>,
  currentIndex: number
): string {
  const answered = answeredSteps.get(index);
  const isCurrent = index === currentIndex;
  const isCompleted = answered !== undefined;
  const isInfoStep = steps[index].stepKind === 'info';

  if (isCompleted) {
    if (isInfoStep) return 'var(--f-progress-done)';
    if (answered.result.isCorrect) return 'var(--f-progress-done)';
    if (answered.result.score > 0) return 'var(--f-warning)';
    return 'var(--f-progress-error)';
  }
  if (isCurrent) return 'var(--f-accent)';
  return 'var(--f-border)';
}

function getGroupBackground(
  group: StepGroup,
  answeredSteps: Map<number, AnsweredStep>,
  steps: StudySessionStep[]
): string {
  let answeredCount = 0;
  let correctCount = 0;
  let partialCount = 0;
  let incorrectCount = 0;

  for (let i = group.startIndex; i <= group.endIndex; i++) {
    const answered = answeredSteps.get(i);
    if (answered) {
      answeredCount++;
      if (steps[i].stepKind !== 'info') {
        if (answered.result.isCorrect) {
          correctCount++;
        } else if (answered.result.score > 0) {
          partialCount++;
        } else {
          incorrectCount++;
        }
      }
    }
  }

  if (answeredCount === 0) return 'var(--f-border)';
  if (answeredCount < group.size) return 'var(--f-accent)'; // partial progress
  // All answered — determine color from mix
  const kinds = [correctCount > 0, partialCount > 0, incorrectCount > 0].filter(Boolean).length;
  if (kinds > 1) return 'var(--f-progress-mixed)';
  if (incorrectCount > 0) return 'var(--f-progress-error)';
  if (partialCount > 0) return 'var(--f-warning)';
  return 'var(--f-progress-done)';
}

// ─── Hover label ────────────────────────────────────────────────────────────

function SegmentLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100"
      style={{
        bottom: 'calc(100% + 4px)',
        fontSize: '0.6rem',
        color: 'var(--f-fg-muted)',
        fontFamily: 'var(--f-font-sans)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

// ─── Individual segment (shared by both modes) ─────────────────────────────

interface SegmentProps {
  index: number;
  background: string;
  isCurrent: boolean;
  isNavigable: boolean;
  label: string;
  onGoToStep: (index: number) => void;
}

function Segment({ index, background, isCurrent, isNavigable, label, onGoToStep }: SegmentProps) {
  if (isCurrent) {
    return (
      <motion.div
        key={index}
        className="group relative min-w-1 flex-1 rounded-full"
        style={{ background, overflow: 'visible' }}
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <SegmentLabel>{index + 1}</SegmentLabel>
      </motion.div>
    );
  }

  if (isNavigable) {
    return (
      <div
        key={index}
        role="button"
        tabIndex={0}
        aria-label={label}
        className="group relative min-w-1 flex-1 cursor-pointer rounded-full hover:opacity-80"
        style={{
          background,
          transition: 'background-color 0.3s ease-out, opacity 0.15s ease',
          overflow: 'visible',
        }}
        onClick={() => onGoToStep(index)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onGoToStep(index);
          }
        }}
      >
        <SegmentLabel>{index + 1}</SegmentLabel>
      </div>
    );
  }

  return (
    <div
      key={index}
      className="group relative min-w-1 flex-1 rounded-full"
      style={{ background, transition: 'background-color 0.3s ease-out', overflow: 'visible' }}
    >
      <SegmentLabel>{index + 1}</SegmentLabel>
    </div>
  );
}

// ─── Flat mode (≤15 steps) ──────────────────────────────────────────────────

function FlatProgressBar() {
  const { state, dispatch, frontier } = useSession();
  const { steps, currentIndex, answeredSteps } = state;

  const handleGoToStep = useCallback(
    (index: number) => dispatch({ type: 'GO_TO_STEP', index }),
    [dispatch]
  );

  return (
    <div className="group/bar -my-3 py-3">
      <nav
        aria-label="Session progress"
        className="flex h-1.5 flex-row items-stretch gap-[2px] overflow-visible group-hover/bar:h-3"
        style={{ transition: 'height 400ms cubic-bezier(0.22, 1, 0.36, 1)', willChange: 'height' }}
      >
        {steps.map((_, index) => {
          const answered = answeredSteps.get(index);
          const isCurrent = index === currentIndex;
          const isCompleted = answered !== undefined;
          const isInfoStep = steps[index].stepKind === 'info';
          const background = getStepBackground(index, steps, answeredSteps, currentIndex);
          const label = isCompleted
            ? isInfoStep
              ? `Step ${index + 1}, viewed`
              : `Step ${index + 1}, ${answered.result.isCorrect ? 'correct' : answered.result.score > 0 ? 'partially correct' : 'incorrect'}`
            : `Step ${index + 1}`;

          return (
            <Segment
              key={index}
              index={index}
              background={background}
              isCurrent={isCurrent}
              isNavigable={isCompleted || index === frontier}
              label={label}
              onGoToStep={handleGoToStep}
            />
          );
        })}
      </nav>
    </div>
  );
}

// ─── Grouped mode (16+ steps) ───────────────────────────────────────────────

function GroupedProgressBar() {
  const { state, dispatch, frontier } = useSession();
  const { steps, currentIndex, answeredSteps } = state;

  const handleGoToStep = useCallback(
    (index: number) => dispatch({ type: 'GO_TO_STEP', index }),
    [dispatch]
  );

  const groups = useMemo(() => createGroups(steps.length), [steps.length]);

  const activeGroupIndex = useMemo(
    () => groups.findIndex((g) => currentIndex >= g.startIndex && currentIndex <= g.endIndex),
    [groups, currentIndex]
  );

  return (
    <div className="group/bar -my-3 py-3">
      <div className="flex flex-row items-center gap-[3px] overflow-visible">
        <nav
          aria-label="Session progress"
          className="flex h-1.5 min-w-0 flex-1 flex-row items-stretch gap-[3px] overflow-visible group-hover/bar:h-3"
          style={{
            transition: 'height 400ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'height',
          }}
        >
          <AnimatePresence initial={false}>
            {groups.map((group, gi) => {
              const isActive = gi === activeGroupIndex;

              if (isActive) {
                // Expanded: show individual segments for this group
                return (
                  <motion.div
                    key={`group-${gi}`}
                    className="flex flex-row items-stretch gap-[2px]"
                    initial={{ flex: 1 }}
                    animate={{ flex: 3 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    layout
                  >
                    {Array.from({ length: group.size }, (_, i) => {
                      const stepIndex = group.startIndex + i;
                      const answered = answeredSteps.get(stepIndex);
                      const isCurrent = stepIndex === currentIndex;
                      const isCompleted = answered !== undefined;
                      const isInfoStep = steps[stepIndex].stepKind === 'info';
                      const background = getStepBackground(
                        stepIndex,
                        steps,
                        answeredSteps,
                        currentIndex
                      );
                      const label = isCompleted
                        ? isInfoStep
                          ? `Step ${stepIndex + 1}, viewed`
                          : `Step ${stepIndex + 1}, ${answered.result.isCorrect ? 'correct' : answered.result.score > 0 ? 'partially correct' : 'incorrect'}`
                        : `Step ${stepIndex + 1}`;

                      return (
                        <Segment
                          key={stepIndex}
                          index={stepIndex}
                          background={background}
                          isCurrent={isCurrent}
                          isNavigable={isCompleted || stepIndex === frontier}
                          label={label}
                          onGoToStep={handleGoToStep}
                        />
                      );
                    })}
                  </motion.div>
                );
              }

              // Compressed: single segment for the whole group
              const background = getGroupBackground(group, answeredSteps, steps);

              // Group is navigable if it contains any answered step or the frontier
              const hasFrontier = frontier >= group.startIndex && frontier <= group.endIndex;
              const hasAnswered = Array.from(
                { length: group.size },
                (_, i) => group.startIndex + i
              ).some((i) => answeredSteps.has(i));
              const isGroupNavigable = hasFrontier || hasAnswered;

              // Target: frontier if in group, otherwise first unanswered, otherwise group start
              let targetIndex = group.startIndex;
              if (isGroupNavigable) {
                for (let i = group.startIndex; i <= group.endIndex; i++) {
                  if (!answeredSteps.has(i)) {
                    targetIndex = i;
                    break;
                  }
                }
              }

              return (
                <motion.div
                  key={`group-${gi}`}
                  className={`group relative rounded-full${isGroupNavigable ? ' cursor-pointer hover:opacity-80' : ''}`}
                  style={{
                    background,
                    transition: 'background-color 0.3s ease-out',
                    overflow: 'visible',
                  }}
                  initial={{ flex: 3 }}
                  animate={{ flex: 1 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  layout
                  {...(isGroupNavigable
                    ? {
                        role: 'button' as const,
                        tabIndex: 0,
                        'aria-label': `Steps ${group.startIndex + 1}–${group.endIndex + 1}`,
                        onClick: () => handleGoToStep(targetIndex),
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleGoToStep(targetIndex);
                          }
                        },
                      }
                    : { 'aria-label': `Steps ${group.startIndex + 1}–${group.endIndex + 1}` })}
                >
                  <SegmentLabel>
                    {group.startIndex + 1}–{group.endIndex + 1}
                  </SegmentLabel>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </nav>

        {/* Step counter */}
        <span
          className="shrink-0 tabular-nums"
          style={{
            fontSize: 'var(--f-text-xs)',
            color: 'var(--f-fg-muted)',
            fontFamily: 'var(--f-font-sans)',
          }}
        >
          {currentIndex + 1}/{steps.length}
        </span>
      </div>
    </div>
  );
}

// ─── Public component ───────────────────────────────────────────────────────

export function SegmentedProgressBar() {
  const { state } = useSession();
  return state.steps.length >= GROUP_THRESHOLD ? <GroupedProgressBar /> : <FlatProgressBar />;
}
